import React, { useEffect } from 'react';
import {
  initializeAnalytics,
  trackPageView,
  ANALYTICS_CONFIG,
} from '../../services/analytics';

/** Page types that map directly to ANALYTICS_CONFIG.PAGES keys. */
type UBTPageKey = keyof typeof ANALYTICS_CONFIG.PAGES;

interface AnalyticsRouteProps {
  /** React children to render inside this wrapper. */
  children: React.ReactNode;
  /** Key from ANALYTICS_CONFIG.PAGES identifying which page this route represents. */
  pageKey: UBTPageKey;
  /** Optional human-readable content title for the page (used in digitalData.page.content.title). */
  contentTitle?: string;
}

/**
 * React Router route wrapper that fires a CJA page-view event on every navigation.
 *
 * Usage:
 * ```tsx
 * <Route
 *   path="/home/my-teams"
 *   element={
 *     <AnalyticsRoute pageKey="MY_TEAMS">
 *       <MyTeams />
 *     </AnalyticsRoute>
 *   }
 * />
 * ```
 *
 * On mount (and whenever pageKey changes):
 * 1. Calls initializeAnalytics() once per session to set up digitalData.
 * 2. Fires _satellite.kpCustomEvent("cjaCustomPageview", { pageName }) for this route.
 */
const AnalyticsRoute: React.FC<AnalyticsRouteProps> = ({ children, pageKey, contentTitle }) => {
  // User identity — read from localStorage set by the auth service
  const nuid = localStorage.getItem('userId') ?? '';
  const email = localStorage.getItem('userEmail') ?? '';

  // Initialize analytics once after the user's identity becomes available
  useEffect(() => {
    if (nuid) {
      initializeAnalytics({ nuid, email });
    }
  }, [nuid, email]);

  // Fire page-view event on every route change
  useEffect(() => {
    try {
      const relativePageKey = ANALYTICS_CONFIG.PAGES[pageKey];
      trackPageView(relativePageKey, {
        contentTitle: contentTitle ?? relativePageKey,
        contentType: 'page',
      });
    } catch (error) {
      console.error(`AnalyticsRoute: failed to track page '${pageKey}'`, error);
    }
  }, [pageKey, contentTitle]);

  return <>{children}</>;
};

export default AnalyticsRoute;
